import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getControls, getFrameworks, getCurrentTenantFrameworks } from '@/services/grc';
import Pagination from '@/components/Pagination';
import { 
  Search, Filter, ChevronRight, CheckCircle2, Clock, XCircle, AlertCircle, 
  Shield, Layers, BadgeCheck, Lock, HeartPulse, CreditCard, Landmark, Brain,
  Scale, Building2, Fingerprint, Network, ShieldCheck, Server, Cloud,
  CircuitBoard, Cpu, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Framework-specific icon mapping
const FRAMEWORK_ICONS = {
  'ISO27001': Shield,
  'SOC2': BadgeCheck,
  'GDPR': Lock,
  'HIPAA': HeartPulse,
  'PCI-DSS': CreditCard,
  'NIST-CSF': Landmark,
  'ISO42001': Brain,
  'HITRUST-CSF': HeartPulse,
  'CCPA': Lock,
  'SOX': Scale,
  'DORA': Building2,
  'FEDRAMP': Landmark,
  'CMMC': Shield,
  'CJIS': Fingerprint,
  'NIS2': Network,
  'CYBER-ESSENTIALS': ShieldCheck,
  'CIS-V8': Server,
  'CSA-STAR': Cloud,
  'COBIT': CircuitBoard,
  'NIST-AI-RMF': Cpu,
  'USPCF': Layers,
};

// Framework-specific color mapping
const FRAMEWORK_COLORS = {
  'ISO27001': 'bg-blue-600',
  'SOC2': 'bg-indigo-600',
  'GDPR': 'bg-purple-600',
  'HIPAA': 'bg-red-500',
  'PCI-DSS': 'bg-green-600',
  'NIST-CSF': 'bg-amber-600',
  'ISO42001': 'bg-pink-600',
  'HITRUST-CSF': 'bg-rose-500',
  'CCPA': 'bg-violet-600',
  'SOX': 'bg-orange-600',
  'DORA': 'bg-cyan-600',
  'FEDRAMP': 'bg-blue-700',
  'CMMC': 'bg-emerald-600',
  'CJIS': 'bg-slate-700',
  'NIS2': 'bg-teal-600',
  'CYBER-ESSENTIALS': 'bg-lime-600',
  'CIS-V8': 'bg-gray-700',
  'CSA-STAR': 'bg-sky-600',
  'COBIT': 'bg-fuchsia-600',
  'NIST-AI-RMF': 'bg-purple-700',
  'USPCF': 'bg-slate-800',
};

const ControlsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [controls, setControls] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [enabledFrameworks, setEnabledFrameworks] = useState([]);
  const [selectedFrameworkDomains, setSelectedFrameworkDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initialize filters from URL so Back-from-detail preserves state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    framework_id: searchParams.get('framework') || '',
    domain: searchParams.get('domain') || '',
    status: searchParams.get('status') || '',
    owner: searchParams.get('owner') || ''
  });

  // Pagination state (also restored from URL)
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') || '20', 10));
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Keep URL in sync with filters/pagination so navigation preserves them
  useEffect(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.framework_id && filters.framework_id !== 'all') params.framework = filters.framework_id;
    if (filters.domain && filters.domain !== 'all') params.domain = filters.domain;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.owner) params.owner = filters.owner;
    if (currentPage !== 1) params.page = String(currentPage);
    if (pageSize !== 20) params.pageSize = String(pageSize);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    loadFrameworks();
    loadEnabledFrameworks();
  }, []);

  useEffect(() => {
    loadControls();
  }, [currentPage, pageSize, filters.framework_id, filters.domain, filters.status, filters.owner]);

  // Update domains when framework changes
  useEffect(() => {
    if (filters.framework_id && filters.framework_id !== 'all') {
      const framework = enabledFrameworks.find(f => f.code === filters.framework_id);
      if (framework && framework.domains) {
        setSelectedFrameworkDomains(framework.domains);
      } else {
        setSelectedFrameworkDomains([]);
      }
    } else {
      setSelectedFrameworkDomains([]);
    }
  }, [filters.framework_id, enabledFrameworks]);

  const loadFrameworks = async () => {
    try {
      const data = await getFrameworks();
      setFrameworks(data.items || data);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const loadEnabledFrameworks = async () => {
    try {
      const data = await getCurrentTenantFrameworks();
      setEnabledFrameworks(data.frameworks || []);
    } catch (error) {
      console.error('Error loading enabled frameworks:', error);
    }
  };

  const loadControls = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize
      };
      if (filters.framework_id && filters.framework_id !== 'all') params.framework_id = filters.framework_id;
      if (filters.domain && filters.domain !== 'all') params.domain = filters.domain;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.owner) params.owner = filters.owner;
      
      const data = await getControls(params);
      if (data.items) {
        setControls(data.items);
        setTotalItems(data.total);
        setTotalPages(data.total_pages);
      } else {
        setControls(data);
        setTotalItems(data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading controls:', error);
      toast.error('Failed to load controls');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleFrameworkChange = (value) => {
    setFilters({ ...filters, framework_id: value, domain: '' });
  };

  const filteredControls = controls.filter(control => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      (control.control_id && control.control_id.toLowerCase().includes(searchLower)) ||
      (control.control_name && control.control_name.toLowerCase().includes(searchLower)) ||
      (control.domain && control.domain.toLowerCase().includes(searchLower))
    );
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      implemented: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, label: 'Implemented' },
      in_progress: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, label: 'In Progress' },
      not_implemented: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertCircle, label: 'Not Implemented' },
      not_applicable: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: XCircle, label: 'N/A' }
    };
    const config = statusConfig[status] || statusConfig.not_implemented;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getFrameworkBadge = (control) => {
    const code = control.framework_code || control.framework_id;
    const name = control.framework_name;
    const Icon = FRAMEWORK_ICONS[code] || Shield;
    const color = FRAMEWORK_COLORS[code] || 'bg-gray-600';
    return (
      <Badge variant="secondary" className="text-xs flex items-center gap-1" title={name}>
        <span className={`w-2 h-2 rounded-full ${color}`}></span>
        {code || 'N/A'}
      </Badge>
    );
  };

  const uniqueDomains = [...new Set(controls.map(c => c.domain).filter(Boolean))];
  const selectedFrameworkInfo = enabledFrameworks.find(f => f.code === filters.framework_id);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Controls Library</h1>
          <p className="text-gray-600 mt-1">
            Browse and manage compliance controls across {enabledFrameworks.length} enabled frameworks
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/grc/uspcf')} variant="outline">
            <Layers className="w-4 h-4 mr-2" />
            USPCF Matrix
          </Button>
          <Button onClick={() => navigate('/grc/frameworks')} variant="outline">
            <Shield className="w-4 h-4 mr-2" />
            Frameworks
          </Button>
        </div>
      </div>

      {/* Selected Framework Info Banner */}
      {selectedFrameworkInfo && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {React.createElement(FRAMEWORK_ICONS[selectedFrameworkInfo.code] || Shield, {
                  className: `w-8 h-8 text-white p-1.5 rounded-lg ${FRAMEWORK_COLORS[selectedFrameworkInfo.code] || 'bg-gray-600'}`
                })}
                <div>
                  <h3 className="font-semibold text-blue-900">{selectedFrameworkInfo.name}</h3>
                  <p className="text-sm text-blue-700">
                    {selectedFrameworkInfo.total_controls || 0} controls • {selectedFrameworkInfo.domains?.length || selectedFrameworkInfo.total_domains || 0} domains
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleFrameworkChange('all')}
              >
                Clear Filter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search controls..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="controls-search-input"
              />
            </div>
            
            {/* Framework Dropdown with icons - ENABLED FRAMEWORKS ONLY */}
            <Select 
              value={filters.framework_id || 'all'} 
              onValueChange={handleFrameworkChange}
            >
              <SelectTrigger data-testid="framework-select">
                <SelectValue placeholder="All Enabled Frameworks" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    All Enabled Frameworks ({enabledFrameworks.length})
                  </div>
                </SelectItem>
                {enabledFrameworks.length > 0 && (
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                    Enabled Frameworks
                  </div>
                )}
                {enabledFrameworks.map(fw => {
                  const Icon = FRAMEWORK_ICONS[fw.code] || Shield;
                  const color = FRAMEWORK_COLORS[fw.code] || 'bg-gray-600';
                  return (
                    <SelectItem key={fw.code} value={fw.code}>
                      <div className="flex items-center gap-2 w-full">
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${color}`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <span className="flex-1">{fw.name}</span>
                        <Badge variant="outline" className="text-xs ml-2">
                          {fw.total_controls || 0}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })}
                {enabledFrameworks.length === 0 && (
                  <div className="px-2 py-3 text-sm text-gray-500 text-center">
                    No frameworks enabled. Enable one from the Frameworks page.
                  </div>
                )}
              </SelectContent>
            </Select>

            {/* Domain Dropdown - Shows domains from selected framework */}
            <Select value={filters.domain || 'all'} onValueChange={(value) => setFilters({ ...filters, domain: value })}>
              <SelectTrigger data-testid="domain-select">
                <SelectValue placeholder="All Domains" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {selectedFrameworkDomains.length > 0 
                    ? `All Domains (${selectedFrameworkDomains.length})` 
                    : 'All Domains'
                  }
                </SelectItem>
                {selectedFrameworkDomains.length > 0 ? (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                      {selectedFrameworkInfo?.name} Domains
                    </div>
                    {selectedFrameworkDomains.map(domain => (
                      <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                    ))}
                  </>
                ) : (
                  uniqueDomains.map(domain => (
                    <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value })}>
              <SelectTrigger data-testid="status-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="implemented">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Implemented
                  </div>
                </SelectItem>
                <SelectItem value="in_progress">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    In Progress
                  </div>
                </SelectItem>
                <SelectItem value="not_implemented">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-500" />
                    Not Implemented
                  </div>
                </SelectItem>
                <SelectItem value="not_applicable">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-yellow-500" />
                    N/A
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={loadControls} variant="outline" data-testid="apply-filters-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Control ID</TableHead>
                  <TableHead>Control Name</TableHead>
                  <TableHead>Framework</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredControls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <AlertCircle className="w-12 h-12 text-gray-300" />
                        <div>
                          <p className="font-medium">No controls found</p>
                          <p className="text-sm mt-1">
                            Enable a framework to auto-populate controls
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={() => navigate('/grc/frameworks')}
                          className="mt-2"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Go to Frameworks
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredControls.map((control) => (
                    <TableRow 
                      key={control.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/grc/controls/${control.id}`, { state: { from: `${location.pathname}${location.search}` } })}
                      data-testid={`control-row-${control.control_id}`}
                    >
                      <TableCell className="font-mono text-sm font-medium">{control.control_id}</TableCell>
                      <TableCell className="max-w-xs truncate">{control.control_name}</TableCell>
                      <TableCell>{getFrameworkBadge(control)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{control.domain}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{control.owner || '-'}</TableCell>
                      <TableCell>{getStatusBadge(control.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/grc/controls/${control.id}`, { state: { from: `${location.pathname}${location.search}` } });
                          }}
                        >
                          View <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default ControlsPage;
